import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'

export interface ProjectFormData {
  name: string
  description: string
}

interface NewProjectDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (formData: ProjectFormData) => void
  loading?: boolean
  error?: string | null
  organizationId?: string | null
}

export const NewProjectDialog: React.FC<NewProjectDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  error = null,
  organizationId = null,
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
  })

  const handleInputChange = (field: keyof ProjectFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    
    // Basic form validation
    if (!formData.name.trim()) {
      return // Let the required attribute handle this
    }

    // Call parent's submit handler
    onSubmit({
      name: formData.name.trim(),
      description: formData.description.trim(),
    })
  }

  const handleClose = () => {
    if (!loading) {
      setFormData({ name: '', description: '' })
      onClose()
    }
  }

  const resetForm = () => {
    setFormData({ name: '', description: '' })
  }

  // Reset form when dialog closes successfully (when not loading and no error)
  React.useEffect(() => {
    if (!open && !loading && !error) {
      resetForm()
    }
  }, [open, loading, error])

  // Show organization requirement if not in organization
  if (!organizationId) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Organization Required
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You must be a member of an organization to create projects.
            </Alert>
            
            <Typography variant="body1" gutterBottom>
              Projects in Wave are organization-scoped to ensure proper access control and collaboration.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use the **organization switcher** in the top navigation to create a new organization or join an existing one.
            </Typography>
            
            <Typography variant="body2" color="text.secondary">
              **Steps to get started:**
            </Typography>
            <Box component="ol" sx={{ pl: 2, mt: 1 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Click your profile icon in the top-right corner
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Select "Manage Organizations" from the dropdown
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Create a new organization or accept an invitation
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Return here to create your first project
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          Create New Project
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <TextField
              autoFocus
              fullWidth
              label="Project Name"
              value={formData.name}
              onChange={handleInputChange('name')}
              margin="normal"
              required
              disabled={loading}
              placeholder="Enter a descriptive name for your project"
              helperText="Choose a name that clearly identifies this drilling project"
            />
            
            <TextField
              fullWidth
              label="Description (Optional)"
              value={formData.description}
              onChange={handleInputChange('description')}
              margin="normal"
              multiline
              rows={3}
              disabled={loading}
              placeholder="Describe the project objectives, location, or any relevant details"
              helperText="Add context to help team members understand the project scope"
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={loading || !formData.name.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
} 